import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./learningStyle.css";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchLsQuizData } from "../../functions/fetchLsQuizData";
import { submitLsQuiz } from "../../functions/submitLsQuiz";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import { checkLsPlayed } from "../../functions/checkLsPlayed";
import { lsPlayedKey, lsLastResultIdKey } from "../../functions/lsPlayedFlag";
import { useDocumentTitle } from "../../functions/useDocumentTitle";

const USER_DATA_KEY = "userData";
const OPTION_KEYS = ["option_one", "option_two", "option_three", "option_four", "option_five", "option_six"];
const DEMO_QUESTION_COUNT = 2;
const EXIT_ANIM_MS = 420;
const TYPE_MS_PER_CHAR = 35;
const OPTION_STAGGER_MS = 180;

/** Fixed pastel palette, cycled by option position — matches the reference design's 4-card grid. */
const OPTION_COLORS = ["#f8bfd2", "#bfe8bc", "#f0d9a3", "#bcc9f2", "#ddc2f2", "#bdeee3"];

function readSessionUserData() {
  try {
    return JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function isDemoUser(userData) {
  const role = String(userData?.role || userData?.source || "").toLowerCase();
  return role === "demobypass" || role === "demo";
}

function formatElapsed(totalSeconds) {
  const s = Math.max(0, Number(totalSeconds) || 0);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

/** Inverse of formatElapsed — "MM:SS" back to total seconds, so a resume can pick up the timer where it left off. */
function parseElapsed(timeStr) {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(":").map((n) => parseInt(n, 10));
  if (parts.length !== 2 || parts.some((n) => Number.isNaN(n))) return 0;
  const [mm, ss] = parts;
  return mm * 60 + ss;
}

/** Only render options with a real value — old app's "not null / not literal NULL string" filter. */
function visibleOptions(question) {
  return OPTION_KEYS.map((key, idx) => ({ pos: idx, text: question[key] })).filter(
    (o) => o.text != null && String(o.text).trim() !== "" && String(o.text).trim().toUpperCase() !== "NULL"
  );
}

function Quiz() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const userData = useMemo(() => readSessionUserData(), []);

  const [settings, setSettings] = useState(null);
  useDocumentTitle(settings?.tab_name);
  const [questions, setQuestions] = useState([]);
  const [statId, setStatId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedPos, setSelectedPos] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [answerPositions, setAnswerPositions] = useState([]);
  const [questionIds, setQuestionIds] = useState([]);
  const [exiting, setExiting] = useState(false);
  const [typedQuestion, setTypedQuestion] = useState("");
  // Ref (not state) so every call always sees the latest id synchronously, with no
  // stale-closure risk across the rapid-fire per-question submit calls.
  const reportIdRef = useRef(userData?.reportId || null);

  useEffect(() => {
    if (!adminToken) {
      navigate("/login");
      return;
    }
    (async () => {
      try {
        // Refuse to (re)start the quiz if this user already has a completed attempt —
        // catches a browser Back press landing here after Thank You, a bookmarked/typed
        // /quiz URL, or a second login. Backend-verified, not just a client-side flag,
        // so it still holds even if sessionStorage was cleared.
        const userIdForCheck = userData?.userId || userData?.userid || userData?.id || null;
        const playedCheck = await checkLsPlayed({ token: adminToken, userId: userIdForCheck });
        if (playedCheck?.played) {
          sessionStorage.setItem(lsPlayedKey(adminToken), "true");
          if (playedCheck.id) sessionStorage.setItem(lsLastResultIdKey(adminToken), String(playedCheck.id));
          navigate("/thankyou", { replace: true });
          return;
        }

        // A `stat` row is created (or resumed) here, right at the start, with the
        // fixed question set already locked in — every Submit afterwards just
        // updates that same row instead of inserting a new one at the end.
        const res = await fetchLsQuizData({
          token: adminToken,
          userId: userData?.userId || userData?.userid || userData?.id || null,
          email: userData?.email || null,
          userType: userData?.role || userData?.source || null,
          reportId: userData?.reportId || null,
        });
        const allQuestions = res.questions || [];
        if (allQuestions.length === 0) {
          Swal.fire("No Questions Found", "This quiz has no questions configured yet.", "info").then(() => {
            navigate("/rules");
          });
          return;
        }
        const demo = isDemoUser(userData);
        const limited = demo ? allQuestions.slice(0, DEMO_QUESTION_COUNT) : allQuestions;
        setSettings(res.settings || null);
        setQuestions(limited);
        setStatId(res.id || null);

        // A resumed attempt may already have an external reportid — reuse it so the
        // create-report call below never fires twice for the same attempt.
        if (res.report_id) {
          reportIdRef.current = res.report_id;
        }

        // Pick the timer back up from where it was last saved, instead of restarting at 0.
        const resumeSeconds = parseElapsed(res.resume_time_taken);
        if (resumeSeconds > 0) {
          setElapsed(resumeSeconds);
        }

        // Resume mid-quiz progress (left the page, refreshed, etc.) instead of restarting —
        // `answer[i]` always corresponds to the i-th question in the fixed set (see
        // submitLsQuiz), so the position within that array is exactly where to continue.
        const resumePositions = Array.isArray(res.resume_answer_positions) ? res.resume_answer_positions : [];
        if (resumePositions.length > 0) {
          const resumeIds = limited.slice(0, resumePositions.length).map((q) => q.id);
          setAnswerPositions(resumePositions);
          setQuestionIds(resumeIds);

          if (resumePositions.length >= limited.length) {
            // Every question was already answered but the row never got finalized
            // (e.g. the final Submit's response never arrived) — finish it now.
            setLoading(false);
            persistProgress(resumePositions, resumeIds, true, res.id);
            return;
          }
          setCurrentIndex(resumePositions.length);
        }

        // Create the external report id right at the start (not at the end) — every
        // question's Submit afterwards just updates this same reportId's score.
        if (!reportIdRef.current && res.id) {
          createInitialReport(res.id);
        }
      } catch (e) {
        Swal.fire("Error", e.message || "Failed to load quiz", "error");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  useEffect(() => {
    if (loading || questions.length === 0) return undefined;
    const interval = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(interval);
  }, [loading, questions.length]);

  const currentQuestion = questions[currentIndex];
  const options = useMemo(() => (currentQuestion ? visibleOptions(currentQuestion) : []), [currentQuestion]);

  // Type the question out letter by letter each time a new question appears.
  useEffect(() => {
    if (!currentQuestion) return undefined;
    const fullText = currentQuestion.question_name || "";
    setTypedQuestion("");
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      setTypedQuestion(fullText.slice(0, i));
      if (i >= fullText.length) clearInterval(interval);
    }, TYPE_MS_PER_CHAR);
    return () => clearInterval(interval);
  }, [currentQuestion]);

  /**
   * Creates the external report exactly once, right at quiz start — sendReport() with no
   * existing reportId POSTs (creates); every later call passes this same id, so they PUT
   * (update) instead of creating a new report per question.
   */
  const createInitialReport = async (currentStatId) => {
    try {
      const response = await sendReport({
        sessionId: userData?.sessionId,
        organizationId: userData?.organizationId,
        userId: userData?.userId || userData?.userid || userData?.id || null,
        name: userData?.name || null,
        role: userData?.role || userData?.source || null,
        token: userData?.token,
        reportId: null,
        gameId: userData?.gameId || null,
        points: 0,
        time: "00:00",
      });
      const newId = extractReportIdFromResponse(response);
      if (newId) {
        reportIdRef.current = newId;
        await submitLsQuiz({ payload: { id: currentStatId, report_id: newId }, token: adminToken });
      }
    } catch (e) {
      console.error("Failed to create external report:", e);
    }
  };

  /** Pushes the current cumulative score to the external tracker — best-effort, never blocks the quiz. */
  const syncExternalReport = async (currentPoints) => {
    try {
      const totalPoints = Object.values(currentPoints || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);
      const response = await sendReport({
        sessionId: userData?.sessionId,
        organizationId: userData?.organizationId,
        userId: userData?.userId || userData?.userid || userData?.id || null,
        name: userData?.name || null,
        role: userData?.role || userData?.source || null,
        token: userData?.token,
        reportId: reportIdRef.current,
        gameId: userData?.gameId || null,
        points: totalPoints,
        time: formatElapsed(elapsed),
      });
      const id = extractReportIdFromResponse(response);
      if (id) reportIdRef.current = id;
    } catch (e) {
      console.error("sendReport failed:", e);
    }
  };

  /**
   * Updates the same `stat` row and syncs the score API on every Submit — not just the
   * last one. Takes an explicit id (rather than only reading the `statId` state) because
   * the resume-on-mount path can call this in the same tick as `setStatId`, before that
   * state update has actually landed.
   */
  const persistProgress = async (positions, ids, isLast, idOverride) => {
    const targetId = idOverride ?? statId;
    if (!targetId) return;
    setSubmitting(true);
    try {
      const payload = {
        id: targetId,
        answer_positions: positions,
        question_ids: ids,
        time_taken: formatElapsed(elapsed),
        is_final: isLast,
      };
      const res = await submitLsQuiz({ payload, token: adminToken });

      await syncExternalReport(res.points);

      if (isLast) {
        sessionStorage.setItem(lsPlayedKey(adminToken), "true");
        sessionStorage.setItem(lsLastResultIdKey(adminToken), String(targetId));
        // replace, not push — removes /quiz from history so a Back press from
        // Thank You can't land back in the finished quiz.
        navigate("/thankyou", { replace: true });
      }
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to submit quiz", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const selectOption = (pos) => {
    if (submitting) return;
    setSelectedPos(pos);
  };

  const handleSubmit = () => {
    if (selectedPos == null) {
      Swal.fire("Please select an option", "", "warning");
      return;
    }
    if (exiting || submitting) return;

    const positions = [...answerPositions, selectedPos];
    const ids = [...questionIds, currentQuestion.id];
    setAnswerPositions(positions);
    setQuestionIds(ids);

    const isLast = currentIndex + 1 >= questions.length;

    setExiting(true);
    setTimeout(() => {
      if (!isLast) {
        setCurrentIndex((i) => i + 1);
        setSelectedPos(null);
      }
      setExiting(false);
      persistProgress(positions, ids, isLast);
    }, EXIT_ANIM_MS);
  };

  if (loading) {
    return (
      <div className="ls-page">
        <div className="ls-loading">Loading...</div>
      </div>
    );
  }

  if (!currentQuestion) {
    return null;
  }

  const progressPct = Math.round(((currentIndex + 1) / questions.length) * 100);
  const optionBoxColors = [1, 2, 3, 4, 5, 6].map(
    (n) => settings?.[`option_box_color${n}`] || OPTION_COLORS[n - 1]
  );

  return (
    <div
      className="ls-page ls-quiz-page"
      style={{
        "--ls-text-color": settings?.text_color || "#1f2937",
        "--ls-question-text-color": settings?.question_text_color || "#2d2d2d",
        "--ls-button-color": settings?.button_color || "#e07b39",
        "--ls-button-text-color": settings?.button_text_color || "#ffffff",
        "--ls-answer-color": settings?.answer_color || "#e07b39",
      }}
    >
      <div className="ls-quiz-progress-pill">
        <span className="ls-quiz-progress-count">
          {currentIndex + 1}/{questions.length}
        </span>
        <span className="ls-quiz-progress-track">
          <span className="ls-quiz-progress-fill" style={{ width: `${progressPct}%` }} />
        </span>
      </div>

      <div key={currentIndex} className={`ls-quiz-slide ${exiting ? "ls-quiz-slide-exit" : "ls-quiz-slide-enter"}`}>
        <h2 className="ls-quiz-question-title">{typedQuestion}</h2>

        <div className="ls-quiz-options-grid">
          {options.map((opt, idx) => (
            <div
              key={opt.pos}
              className={`ls-quiz-option-card ${selectedPos === opt.pos ? "selected" : ""}`}
              style={{
                backgroundColor: optionBoxColors[opt.pos % optionBoxColors.length],
                animationDelay: `${idx * OPTION_STAGGER_MS}ms`,
              }}
              onClick={() => selectOption(opt.pos)}
            >
              {opt.text}
            </div>
          ))}
        </div>

        <button type="button" className="ls-quiz-submit-btn" onClick={handleSubmit} disabled={submitting || exiting}>
          {submitting ? "Submitting..." : "Submit"}
        </button>
      </div>
    </div>
  );
}

export default Quiz;
